import { useState, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { DEFAULT_DRAWER_WIDTH, MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH, COLLAPSED_DRAWER_WIDTH } from '../constants';

export const useDrawerState = (isRTL) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  const currentDrawerWidth = isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : drawerWidth;

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleDrawerCollapse = useCallback(() => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
    if (!isDrawerCollapsed) {
      setDrawerWidth(COLLAPSED_DRAWER_WIDTH);
    } else {
      setDrawerWidth(DEFAULT_DRAWER_WIDTH);
    }
  }, [isDrawerCollapsed]);

  const handleMouseDown = useCallback((e) => {
    if (isMobile || isDrawerCollapsed) return;
    setIsResizing(true);
    e.preventDefault();
    e.stopPropagation();
  }, [isMobile, isDrawerCollapsed]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    e.preventDefault();
    
    let newWidth;
    if (isRTL) {
      // For RTL, calculate from the right edge
      newWidth = window.innerWidth - e.clientX;
    } else {
      // For LTR, calculate from the left edge
      newWidth = e.clientX;
    }
    
    if (newWidth >= MIN_DRAWER_WIDTH && newWidth <= MAX_DRAWER_WIDTH) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing, isRTL]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Effect to handle mouse events during resize
  useEffect(() => {
    if (isResizing) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => handleMouseUp();
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    mobileOpen,
    drawerWidth,
    isDrawerCollapsed,
    isResizing,
    currentDrawerWidth,
    resizeRef,
    isMobile,
    handleDrawerToggle,
    handleDrawerCollapse,
    handleMouseDown
  };
}; 